import { Box, Button, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import IntellioAdvantageChatDrawerContent from '../../components/Chat/IntellioAdvantageChatDrawerContent';
import LearnMore from '../../components/LearnMore';
import { useDrawer } from '../../components/Providers/DrawerProvider';
import { useFetchData } from '../../hooks/useFetchData';
import {
  setCurrentConversation,
  setCurrentMessages,
} from '../../redux/reducers/conversationReducer';
import { createSummaryChat } from '../../services/dealGPT';
import {
  CollectionWithCreator,
  ProjectWithLastScanned,
  ScopePagePair,
  SummarizationFile,
  SummaryAssistantResult,
} from '../../Types/dealGPT';
import { ChatMode } from '../../Types/enums';
import { collectionsPathUrl, summaryChatFilesPathUrl } from '../../util/routesUrls';
import { useChat } from '../Providers/ChatProvider';
import SelectSummaryReferences from './SelectSummaryReferences';

interface IntellioAdvantageChatDrawerProps {
  project: ProjectWithLastScanned;
  initialMode?: ChatMode;
  initialSummaryFileId?: string;
  redirectOnProcess?: boolean;
}

const IntellioAdvantageChatDrawer: React.FC<IntellioAdvantageChatDrawerProps> = ({
  project,
  initialMode = ChatMode.DISCOVERY,
  initialSummaryFileId,
  redirectOnProcess = false,
}) => {
  const dispatch = useDispatch();

  const [currentMode, setCurrentMode] = useState<ChatMode>(initialMode);
  const [selectedCollections, setSelectedCollections] = useState<CollectionWithCreator[]>([]);
  const [selectedScopePages, setSelectedScopePages] = useState<ScopePagePair[]>([]);
  const [selectedSummaryFileIds, setSelectedSummaryFileIds] = useState<string[]>([]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [assistantResult, setAssistantResult] = useState<SummaryAssistantResult | null>(null);
  const { drawerConversationIdRef, inputRef, handleSubmit } = useChat();
  const { addOnClose, closeDrawer } = useDrawer();

  // --- Fetch summary files ---
  const { data: summaryFiles } = useFetchData<SummarizationFile[]>({
    urlPath: summaryChatFilesPathUrl(project.id),
    skip: !project,
  });

  const summaryCollectionIds =
    summaryFiles?.map((sf) => sf.collectionId).filter((cid): cid is string => Boolean(cid)) ?? [];

  const { data: summaryCollections } = useFetchData<CollectionWithCreator[]>({
    urlPath: collectionsPathUrl({ ids: summaryCollectionIds }),
    skip: summaryCollectionIds.length === 0,
  });

  // Cleanup on drawer close
  useEffect(() => {
    const handleClose = () => {
      drawerConversationIdRef.current = null;
      dispatch(setCurrentConversation(undefined));
      dispatch(setCurrentMessages([]));
    };

    addOnClose(handleClose);
  }, [dispatch, addOnClose, drawerConversationIdRef]);

  const filteredSummaryFiles = useMemo(() => {
    if (!summaryFiles) return [];

    if (currentMode === ChatMode.DISCOVERY) {
      const collectionIds = selectedCollections.map((c) => c.id);
      return summaryFiles.filter((f) => f.collectionId && collectionIds.includes(f.collectionId));
    } else {
      return summaryFiles.filter((f) =>
        selectedScopePages.some((p) => p.pageName === f.pageName && p.scope === f.scope)
      );
    }
  }, [summaryFiles, currentMode, selectedCollections, selectedScopePages]);

  // Memoize total documents
  const totalDocuments = useMemo(
    () => filteredSummaryFiles.reduce((acc, file) => acc + 1 + (file.references?.length ?? 0), 0),
    [filteredSummaryFiles]
  );

  const handleStart = async () => {
    if (!project) return;

    setIsProcessing(true);
    try {
      if (
        (currentMode === ChatMode.DISCOVERY && selectedCollections.length === 0) ||
        (currentMode === ChatMode.ANALYSIS && selectedScopePages.length === 0)
      ) {
        console.warn('No selection for summary chat creation.');
        return;
      }

      dispatch(setCurrentConversation(undefined));
      dispatch(setCurrentMessages([]));

      let summaryResult: SummaryAssistantResult;
      let summaryTitle = '';
      if (currentMode === ChatMode.DISCOVERY) {
        const collectionIds = selectedCollections.map((c) => c.id);
        summaryTitle = `Chating with ${selectedCollections.map((c) => c.name).join(', ')}`;
        summaryResult = await createSummaryChat(project.id, { collectionIds });
      } else {
        summaryTitle = `Chating with ${selectedScopePages.map((p) => `${p.scope}`).join(', ')}`;
        summaryResult = await createSummaryChat(project.id, { scopePagePairs: selectedScopePages });
      }

      setSelectedSummaryFileIds(filteredSummaryFiles.map((f) => f.id));
      setAssistantResult(summaryResult);

      if (redirectOnProcess) {
        // Clear previous refs and set input for title generation
        drawerConversationIdRef.current = null;
        if (inputRef.current) inputRef.current.value = summaryTitle;

        // Submit to create conversation, not actually submitting any message
        await handleSubmit({
          isDrawerChat: true,
          vectorStoreId: summaryResult.vectorStoreId,
          redirectOnProcess,
          summaryFileIds: filteredSummaryFiles.map((f) => f.id),
        });
        closeDrawer();
      } else {
        setShowChat(true);
      }
    } catch (error) {
      console.error('Error creating summary chat:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (showChat && assistantResult) {
    return (
      <IntellioAdvantageChatDrawerContent
        vectorStoreId={assistantResult.vectorStoreId}
        project={project}
        isDrawerChat={true}
        activeTab={currentMode}
        collections={selectedCollections.map((p) => p.name)}
        totalDocuments={totalDocuments}
        scopePagePairs={selectedScopePages}
        redirectOnProcess={redirectOnProcess}
        summaryFileIds={selectedSummaryFileIds}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          margin: 2,
        }}
      >
        <Typography>
          Your selections guide what Intellio analyzes. We pull in the documents and prior outputs
          connected to your chosen questions or steps and build a targeted chat experience around
          them.
        </Typography>
        <LearnMore
          link="https://howto.westmonroe.com/a/2015425"
          sx={{ fontSize: '16px', mr: '5px', lineHeight: '16px' }}
          iconSx={{ fontSize: '16px' }}
        />
      </Box>

      {/* Step 1: Choose mode */}
      <Box
        sx={{
          padding: 3,
          border: '1px solid #070154',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          margin: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          1. Choose an option
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={currentMode === ChatMode.DISCOVERY ? 'contained' : 'outlined'}
            onClick={() => setCurrentMode(ChatMode.DISCOVERY)}
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: currentMode === ChatMode.DISCOVERY ? 700 : 400,
              borderRadius: 4,
            }}
          >
            Discovery
          </Button>

          <Button
            variant={currentMode === ChatMode.ANALYSIS ? 'contained' : 'outlined'}
            onClick={() => setCurrentMode(ChatMode.ANALYSIS)}
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: currentMode === ChatMode.ANALYSIS ? 700 : 400,
              borderRadius: 4,
            }}
          >
            Analysis
          </Button>
        </Box>
      </Box>

      {/* Step 2: Select references*/}
      <SelectSummaryReferences
        currentMode={currentMode}
        summaryCollections={summaryCollections ?? []}
        selectedCollections={selectedCollections}
        setSelectedCollections={setSelectedCollections}
        selectedScopePages={selectedScopePages}
        setSelectedScopePages={setSelectedScopePages}
        summaryFiles={summaryFiles ?? []}
        initialSummaryFileId={initialSummaryFileId}
      />

      {/* Step 3: Start */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleStart}
        disabled={
          isProcessing ||
          (currentMode === ChatMode.DISCOVERY && selectedCollections.length === 0) ||
          (currentMode === ChatMode.ANALYSIS && selectedScopePages.length === 0)
        }
        fullWidth
        sx={{ textTransform: 'none', fontWeight: 600, p: '10px', borderRadius: 4, m: 2 }}
      >
        {isProcessing ? <CircularProgress size={24} color="inherit" /> : 'Start Processing'}
      </Button>
    </Box>
  );
};

export default IntellioAdvantageChatDrawer;
